import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import SettingsSelect from '../SettingsSelect/SettingsSelect';
import {
	getActiveLanguage,
	getAvailableLanguages,
} from '../../redux/LanguageSlice/LanguageSelectors';
import { useAppDispatch } from '../../redux/Store';
import LanguageActions from '../../redux/LanguageSlice/LanguageActions';

const messages = defineMessages({
	language: {
		id: 'settings.language',
		defaultMessage: 'Language',
	},
});

interface LanguageNames {
	[key: string]: string;
}

const languageNames: LanguageNames = {
	en: 'English (US)',
	lt: 'Lietuvių (LT)',
	ru: 'Русский (RU)',
	fr: 'Français (FR)',
	it: 'Italiano (IT)',
	ar: 'العربية (AR)',
	'es-ES': 'Español(ES)',
	'uk-UA': 'Українська (UA)',
	et: 'Eesti (ET)',
};

interface Props {
	closeSettings: () => void;
}

const LanguageSwitcher = ({ closeSettings }: Props) => {
	const { formatMessage } = useIntl();
	const activeLanguage = useSelector(getActiveLanguage);
	const availableLanguagesList = useSelector(getAvailableLanguages);
	const dispatch = useAppDispatch();
	const { push } = useHistory();

	const languageOptions = useMemo(
		() =>
			availableLanguagesList.map((language) => ({
				id: language,
				label: `${languageNames[language]}`,
			})),
		[availableLanguagesList]
	);

	const activeOption = useMemo(
		() => languageOptions.find(({ id }) => id === activeLanguage)!,
		[activeLanguage, languageOptions]
	);

	const onLanguageChange = useCallback(
		(option: string) => {
			dispatch(LanguageActions.setLanguage(option));
			const { pathname } = window.location;
			const pathLang = pathname.split('/')[1];
			if (availableLanguagesList.includes(pathLang)) push(pathname.replace(pathLang, option));
			else push(`/${option}${pathname}`);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch]
	);

	return (
		<SettingsSelect
			title={formatMessage(messages.language)}
			options={languageOptions}
			value={activeOption.id}
			onChange={onLanguageChange}
			closeSettings={closeSettings}
		/>
	);
};

export default LanguageSwitcher;
